import { format, startOfDay, subDays } from "@-/com.npmjs.date-fns";
import {
  FSRS_STATE_PALETTE,
  type CardsByType,
  type CharacterStageReview,
  type LearnedGraphemeRow,
  type LearnedStudyCardRow,
  type LearnedWordRow,
  type LearningStatsCatalogData,
  type LearningStatsCoreData,
  type LearningStatsData,
  type LearningStatsReviewHistoryData,
  type ProgressStageKey,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import type { HskVersionKey } from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import {
  buildCoverageTimeline,
  buildDailyReviewSeries,
  buildHskProgress,
  buildLearnedTimeline,
  buildRetentionSeries,
  addDailyRechargePoints,
  computeStreaks,
} from "#learning-stats-series";
import arrayLength from "@-/com.npmjs.extra-array__length";
import {
  buildCharacterStageTimeline,
  buildCoverageStageTimeline,
} from "#build-character-stage-timeline";

/** Every slice at once — the one-shot form used by tests and offline reports. */
export function computeLearningStats(args: {
  cardsByStage: Record<ProgressStageKey, number>;
  cardsByStageAndType: Record<ProgressStageKey, CardsByType>;
  dueTodayCount: number;
  graphemesByStage: Record<ProgressStageKey, readonly string[]>;
  hskTotalsByLevel: Map<number, number>;
  hskVersion: HskVersionKey;
  learnedGraphemes: readonly LearnedGraphemeRow[];
  learnedWords?: readonly LearnedWordRow[];
  reviewedAtList: readonly string[];
  reviewLog: readonly { rating: number; review: string }[];
  stageReviews?: readonly CharacterStageReview[];
  studyCardStateList: readonly number[];
}): LearningStatsData {
  return {
    coverageStageTimeline: buildCoverageStageTimeline(
      args.learnedGraphemes,
      args.stageReviews ?? [],
    ),
    ...computeLearningStatsCore({
      ...args,
      learnedStudyCards: args.learnedGraphemes,
      learnedWords: args.learnedWords ?? [],
    }),
    ...computeLearningStatsCatalog(args),
    ...computeLearningStatsReviewHistory({ ...args, learnedStudyCards: args.learnedGraphemes }),
  };
}

/**
 * The actor-DB-only slice: card tallies and the learned-character list. Pure of the static
 * catalog, so the Stats page can paint from it before any HSK / frequency lookup resolves.
 */
export function computeLearningStatsCore(args: {
  cardsByStage: Record<ProgressStageKey, number>;
  cardsByStageAndType: Record<ProgressStageKey, CardsByType>;
  dueTodayCount: number;
  graphemesByStage: Record<ProgressStageKey, readonly string[]>;
  hskVersion: HskVersionKey;
  learnedStudyCards: readonly LearnedStudyCardRow[];
  learnedWords: readonly LearnedWordRow[];
  studyCardStateList: readonly number[];
}): LearningStatsCoreData {
  const cardsByStateCounts = [0, 0, 0, 0];
  const charactersByStage: Record<ProgressStageKey, number> = {
    familiar: 0,
    mastered: 0,
    new: 0,
    proficient: 0,
  };
  for (const card of args.learnedStudyCards) {
    charactersByStage[card.stage] += 1;
  }
  const wordsByStage: Record<ProgressStageKey, number> = {
    familiar: 0,
    mastered: 0,
    new: 0,
    proficient: 0,
  };
  for (const word of args.learnedWords) {
    wordsByStage[word.stage] += 1;
  }
  for (const state of args.studyCardStateList) {
    const stateIndex = Math.max(0, Math.min(3, state));
    cardsByStateCounts[stateIndex] = (cardsByStateCounts[stateIndex] ?? 0) + 1;
  }

  return {
    cardsByStage: args.cardsByStage,
    cardsByStageAndType: args.cardsByStageAndType,
    cardsByState: FSRS_STATE_PALETTE.map((entry, index) => ({
      color: entry.color,
      label: entry.label,
      value: cardsByStateCounts[index] ?? 0,
    })),
    charactersByStage,
    dueTodayCount: args.dueTodayCount,
    graphemesByStage: args.graphemesByStage,
    hskVersion: args.hskVersion,
    learnedStudyCards: [...args.learnedStudyCards],
    learnedWords: [...args.learnedWords],
    totalLearned: arrayLength(args.learnedStudyCards),
    totalWordsLearned: arrayLength(args.learnedWords),
    wordsByStage,
  };
}

/** The static-catalog slice: HSK progress and corpus-coverage series over the learned set. */
export function computeLearningStatsCatalog(args: {
  hskTotalsByLevel: Map<number, number>;
  hskVersion: HskVersionKey;
  learnedGraphemes: readonly LearnedGraphemeRow[];
}): LearningStatsCatalogData {
  const todayStart = startOfDay(new Date());

  const { lifetimePercent, series: coverageTimeline } = buildCoverageTimeline(
    args.learnedGraphemes,
    todayStart,
  );
  const hskProgress = buildHskProgress(
    args.learnedGraphemes,
    args.hskTotalsByLevel,
    args.hskVersion,
  );
  const learnedTimeline = buildLearnedTimeline(args.learnedGraphemes);

  return {
    coverageTimeline,
    hskProgress,
    learnedGraphemes: args.learnedGraphemes,
    learnedTimeline,
    lifetimeCoveragePercent: Math.round(lifetimePercent * 10) / 10,
  };
}

export function computeLearningStatsReviewHistory(
  args: {
    learnedStudyCards?: readonly LearnedStudyCardRow[];
    reviewedAtList: readonly string[];
    reviewLog: readonly { rating: number; review: string }[];
    stageReviews?: readonly CharacterStageReview[];
  },
  now = new Date(),
): LearningStatsReviewHistoryData {
  const todayStart = startOfDay(now);
  const todayKey = format(todayStart, "yyyy-MM-dd");

  let reviewedTodayCount = 0;
  for (const iso of args.reviewedAtList) {
    if (format(new Date(iso), "yyyy-MM-dd") === todayKey) {
      reviewedTodayCount += 1;
    }
  }

  const sevenDaysAgo = subDays(todayStart, 7).getTime();
  let reviewsLast7d = 0;
  for (const iso of args.reviewedAtList) {
    if (new Date(iso).getTime() >= sevenDaysAgo) {
      reviewsLast7d += 1;
    }
  }

  const dailyRechargePoints = new Map<string, number>();
  for (const entry of args.reviewLog) {
    addDailyRechargePoints(dailyRechargePoints, { rating: entry.rating, reviewedAt: entry.review });
  }
  const streaks = computeStreaks(dailyRechargePoints, todayStart);
  const dailyReviewSeries = buildDailyReviewSeries(args.reviewedAtList, todayStart);
  const { last30dPercent, series: retentionSeries } = buildRetentionSeries(
    args.reviewLog,
    todayStart,
  );

  return {
    characterStageTimeline: buildCharacterStageTimeline(
      args.learnedStudyCards ?? [],
      args.stageReviews ?? [],
      now,
    ),
    currentStreak: streaks.current,
    dailyRechargePoints: Array.from(dailyRechargePoints, ([day, points]) => ({ day, points })).sort(
      (a, b) => a.day.localeCompare(b.day),
    ),
    dailyReviewSeries,
    longestStreak: streaks.longest,
    rechargePointsToday: dailyRechargePoints.get(todayKey) ?? 0,
    retentionLast30dPercent: last30dPercent,
    retentionSeries,
    reviewedTodayCount,
    reviewsLast7d,
    totalReviews: arrayLength(args.reviewedAtList),
  };
}
